import React, { FunctionComponent, useContext, useState } from 'react'
import { Card, List, Modal } from 'antd'
import { Vehicle, CollectionDevice } from 'server'
import EditVehicleForm from '../../components/EditVehicleForm/EditVehicleForm'
import DeviceListItem from './DeviceListItem/DeviceListItem'
import { useListScrollRender } from '../../hooks/ui/useListScrollRender'
import { MapSettingsContext } from '../../contexts/MapSettingsContext'
import MapSettingsContextInterface from '../../contexts/MapSettingsContext/MapSettingsContextInterface'
import { arrayToMap, getAllData, mapToArray } from '../../utils/data'
import DevicesListFilter from './DevicesListFilter/DevicesListFilter'
import { filterDeviceList } from './utils'
import { useLoaderData, useMatch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useFeathersClient } from '@strac/react-client-feathers'
import useLocalStorageValue from '../Map/RightSider/Options/hooks/useLocalStroageValue'
import styled from '@emotion/styled'
import LastDeviceLocationUpdates from '../../components/LastDeviceLocationUpdates/LastDeviceLocationUpdates'
import useDefaultVehicleFilter from '../../hooks/data/useDefaultVehicleFilter'

const StyledContainer = styled.div`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

interface IProps {
  userId?: number
  agencyId?: number
}

const DevicesList: FunctionComponent<any> = (props: IProps) => {

  const { userId, agencyId } = props

  const match = useMatch({ strict: false })
  const { vehicles, vehicleLocations } = useLoaderData({ from: match.routeId ?? '/' })

  const vehicleMap = arrayToMap(vehicles as Vehicle[], 'vehicle_id')

  const { selectedCollections } = useContext(MapSettingsContext) as MapSettingsContextInterface

  const texasMask = useLocalStorageValue('assettrak_texas_mask')

  const client = useFeathersClient()

  const collectionDevicesQuery = {
    collection_id: {
      $in: selectedCollections
    },
    active: true
  }
  const { data: collectionDevices } = useQuery({
    queryKey: ['collection-device', 'find', collectionDevicesQuery],
    queryFn: () => getAllData(client, 'collection-device', collectionDevicesQuery)
  })

  const vehicleIds = (collectionDevices || [])?.map(
    (collectionDevice: CollectionDevice) => collectionDevice.vehicle_id
  )

  const [selectedAgency, setSelectedAgency] = useState<number | undefined>(undefined)

  const [search, setSearch] = useState<string>('')

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle)
    setModalOpen(true)
  }

  const handleClose = () => {
    setSelectedVehicle(undefined)
    setModalOpen(false)
  }

  const filteredVehicleLocations = useDefaultVehicleFilter(vehicles, vehicleLocations)

  const vehicleLocationMap = arrayToMap(filteredVehicleLocations, 'vehicle_id')

  const filtered = filterDeviceList(
    mapToArray(vehicleMap),
    vehicleLocationMap,
    vehicleIds,
    selectedAgency,
    search,
    texasMask
  )

  const renderItem = (vehicle: Vehicle) => {
    const vehicleLocation = vehicleLocationMap.get(vehicle.vehicle_id)
    return <DeviceListItem
      key={vehicle.vehicle_id}
      vehicle={vehicle}
      vehicleLocation={vehicleLocation}
      handleEditVehicle={handleEditVehicle}
      search={search}
    />
  }

  const { ref, end } = useListScrollRender(vehicleMap.size)

  const sorted = filtered.sort((a, b) => {
    if (a.name < b.name) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    return 0
  })

  return (
    <StyledContainer>
      <DevicesListFilter vehicles={filtered} setSelectedAgency={setSelectedAgency} setSearch={setSearch} />
      <Card
        style={{
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
        }}
        styles={{
          body: {
            padding: 0
          }
        }}
      >
        <div
          style={{
            height: '100%',
            overflow: 'auto',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          ref={ref}
        >
          <List
            header={
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{`Devices: ${filtered.length} ${
                  selectedCollections.length
                    ? `(Filtered by ${selectedCollections.length} Collection${
                        selectedCollections.length > 1 ? 's' : ''
                      })`
                    : ''
                }`}</span>
              </div>
            }
            bordered={true}
            dataSource={sorted.slice(0, end)}
            renderItem={renderItem}
          />
        </div>
      </Card>
      <Modal open={modalOpen} onCancel={handleClose} title={selectedVehicle?.name} footer={null}>
        <EditVehicleForm
          key={selectedVehicle?.vehicle_id}
          vehicleId={selectedVehicle?.vehicle_id}
          handleClose={handleClose}
        />
        {selectedVehicle ? <LastDeviceLocationUpdates vehicle={selectedVehicle} /> : null}
      </Modal>
    </StyledContainer>
  )
}

export default DevicesList
